// ============================================================================
// A file share's options turned into a Grant's spec (docs/areas/files.md), and
// a grant read back for the share sheet. Pure; share.test.ts. The secret is
// made and hashed on the device like every link's; a password is hashed by the
// server (POST /api/files/password) and only its argon2id hash is kept.
// ============================================================================

import type { GrantResource } from '@/kinds/grant/apply'
import { fileShareOf, type GrantSpec } from '@/kinds/grant/schema'

export interface ShareOptions {
  /** a link to look at (and download), or a file request into a folder */
  mode: 'link' | 'request'
  name: string
  /** YYYY-MM-DD: the link works until the end of that local day */
  until?: string
  /** downloads before the link ends */
  maxDownloads?: number
  viewOnly?: boolean
  /** a file request's largest file (MB) and how many files in all */
  requestMaxMB?: number
  requestMaxFiles?: number
  /** a line under the file request's heading */
  intro?: string
}

/** The end of a local date as RFC 3339 UTC (a link "until 3 November" works all that day). */
export const endOfDay = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString()
}

/** Problems with the options, said plainly (empty = fine). */
export const shareProblems = (o: ShareOptions, today: string): string[] => {
  const out: string[] = []
  if (o.until && o.until < today) out.push('The end date has passed')
  if (
    o.maxDownloads !== undefined &&
    (!Number.isInteger(o.maxDownloads) || o.maxDownloads < 1 || o.maxDownloads > 100_000)
  )
    out.push('Downloads is a whole number from 1')
  if (o.requestMaxMB !== undefined && (!(o.requestMaxMB > 0) || o.requestMaxMB > 5120))
    out.push('The largest file is up to 5120 MB')
  if (
    o.requestMaxFiles !== undefined &&
    (!Number.isInteger(o.requestMaxFiles) || o.requestMaxFiles < 1 || o.requestMaxFiles > 1000)
  )
    out.push('Files is a whole number from 1 to 1000')
  return out
}

/** The grant spec for a share of `fileId` (a file or folder; a request needs a folder). */
export const shareSpec = (
  fileId: string,
  o: ShareOptions,
  tokenHash: string,
  passwordHash?: string,
): GrantSpec => {
  const base: GrantSpec = {
    name: o.name.trim() || 'File link',
    scopes: [`file:${fileId}:${o.mode === 'request' ? 'drop' : 'read'}`],
    token_hash: tokenHash,
    ...(o.until ? { expires_at: endOfDay(o.until) } : {}),
  }
  if (o.mode === 'request')
    return {
      ...base,
      ...(o.requestMaxMB ? { drop_max_bytes: Math.round(o.requestMaxMB * 1024 * 1024) } : {}),
      ...(o.requestMaxFiles ? { drop_max_files: o.requestMaxFiles } : {}),
      ...(o.intro?.trim() ? { intro: o.intro.trim().slice(0, 300) } : {}),
    }
  return {
    ...base,
    ...(passwordHash ? { password_hash: passwordHash } : {}),
    ...(o.maxDownloads ? { max_downloads: o.maxDownloads } : {}),
    ...(o.viewOnly ? { view_only: true } : {}),
  }
}

/** The shares of one file or folder, newest first. */
export const sharesOf = (grants: readonly GrantResource[], fileId: string): GrantResource[] =>
  grants
    .filter((g) => !g.deleted_at && fileShareOf(g.spec)?.id === fileId)
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))

/** What a share is, in a few words: "Password, 2 of 5 downloads, until 3 Nov". */
export const describeShare = (g: GrantResource, fmtDate: (iso: string) => string): string[] => {
  const fs = fileShareOf(g.spec)
  const parts: string[] = []
  if (fs?.drop) {
    parts.push('File request')
    if (g.spec.drop_max_files) parts.push(`${g.status.drops ?? 0} of ${g.spec.drop_max_files} files in`)
    else if (g.status.drops) parts.push(`${g.status.drops} in`)
  } else {
    if (g.spec.password_hash) parts.push('Password')
    if (g.spec.view_only) parts.push('View only')
    parts.push(`opened ${g.status.use_count}×`)
    if (g.spec.max_downloads) parts.push(`${g.status.downloads ?? 0} of ${g.spec.max_downloads} downloads`)
    else if (g.status.downloads) parts.push(`${g.status.downloads} downloads`)
  }
  if (g.spec.expires_at) parts.push(`until ${fmtDate(g.spec.expires_at)}`)
  return parts
}
